import { getTheme, Icon, IStyle, ITheme, keyframes, mergeStyleSets } from '@fluentui/react';
import React from 'react';
import { IProductTextureModel } from '../../../../entities-generated/models/project/product-texture-model';
import { noWrapStyle } from '../../../../framework/utils/no-wrap-style';
import { BwjPalette } from '../../../../app-core/app-theme';
import { PriceDisplay } from '../../../shop/components/price';
import { ProductVariantLabel } from '../../../shop/components/pdct-variant-label';
import { BwjLinkButton } from '../../../../framework/components/bwj-link-button';

export enum CartProductListingTheme {
  Primary,
  Secondary,
  Grayscale,
}

interface ICartProductListingProps {
  theme: CartProductListingTheme;
  label: string;
  variant?: IProductTextureModel | undefined;
  showNotification: boolean;
  pricing: number | string | undefined;
  title: string;
  imageUrl?: string | undefined;
  onExclude?: () => void;
  onInclude?: () => void;
  onSelected?: () => void;
}

const getStyleClasses = (theme: ITheme, motif: CartProductListingTheme, selectable: boolean) => {
  const root: IStyle = {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
  };

  const thumbnail: IStyle = {
    width: '3rem',
    height: '3.75rem',
    backgroundColor: theme.palette.neutralTertiaryAlt,
    backgroundPosition: '50% 50%',
    backgroundSize: 'cover',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
  if(motif === CartProductListingTheme.Grayscale) {
    thumbnail.filter = 'grayscale(100%)';
  }
  if(selectable) {
    thumbnail.cursor = 'pointer';
  }

  const thumbnailIcon: IStyle = {
    fontSize: '1.5rem',
    color: theme.palette.neutralSecondaryAlt,
    cursor: 'pointer',
  };

  const content: IStyle = {
    flexGrow: 1,
    width: 1,
    boxSizing: 'border-box',
  };

  const topLine: IStyle = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  };

  const motifColor = motif === CartProductListingTheme.Primary
    ? theme.palette.themePrimary
    : motif === CartProductListingTheme.Secondary
      ? BwjPalette.accentPrimary
      : theme.palette.neutralSecondaryAlt;
  const label: IStyle = {
    ...noWrapStyle as any,
    display: 'block',
    position: 'relative',
    margin: 0,
    paddingTop: '0.3rem',
    paddingBottom: 0,
    paddingLeft: '0.6rem',
    paddingRight: '0.6rem',
    height: 28,
    fontSize: theme.fonts.small.fontSize,
    lineHeight: theme.fonts.small.lineHeight,
    color: theme.palette.white,
    fontWeight: 600,
    backgroundColor: motifColor,
    overflow: 'visible',
    boxSizing: 'border-box',

    '& > i': {
      display: 'block',
      width: 0,
      height: 0,
      position: 'absolute',
      right: -28,
      bottom: -28,
      border: '28px solid transparent',
      borderTopColor: motifColor,
    borderLeftWidth: 0,
    },
  };
  if(selectable) {
    (label as any).cursor = 'pointer';
  }

  const pricing: IStyle = {
    padding: '0.3rem',
    display: 'flex',
    flexDirection: 'row',
    gap: '0.75rem',
  };

  const textColor = motif === CartProductListingTheme.Grayscale
      ? theme.palette.neutralSecondaryAlt : theme.palette.neutralPrimary;
  const priceInfo: IStyle = {
    fontSize: theme.fonts.small.fontSize,
    lineHeight: theme.fonts.small.lineHeight,
    color: textColor,
    fontWeight: 600,
  };

  const shakeAnimation = keyframes({
    '0%': {
      transform: 'translateX(0%)',
    },
    '2%': {
      transform: 'translateX(-15%)',
    },
    '6%': {
      transform: 'translateX(15%)',
    },
    '10%': {
      transform: 'translateX(-15%)',
    },
    '14%': {
      transform: 'translateX(15%)',
    },
    '18%': {
      transform: 'translateX(-15%)',
    },
    '22%': {
      transform: 'translateX(15%)',
    },
    '28%': {
      transform: 'translateX(-15%)',
    },
    '32%': {
      transform: 'translateX(15%)',
    },
    '34%': {
      transform: 'translateX(0%)',
    },
    '100%': {
      transform: 'translateX(0%)',
    },
  });
  const notification: IStyle = {
    animation: `${shakeAnimation} 2.75s infinite cubic-bezier(0.53, 0.21, 0.29, 0.67)`,
    fontSize: theme.fonts.mediumPlus.fontSize,
    color: theme.palette.themePrimary,
  };

  const details: IStyle = {
    padding: '0 0.75rem',
  };

  const heading: IStyle = {
    ...noWrapStyle as any,
    fontSize: theme.fonts.medium.fontSize,
    lineHeight: theme.fonts.medium.lineHeight,
    margin: 0,
    marginTop: '0.5rem',
    marginBottom: '0.75rem',
    color: textColor,
    fontWeight: 600,
  };
  if(selectable) {
    (heading as any).cursor = 'pointer';
  }

  const action: IStyle = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',

    '& button': {
      fontSize: theme.fonts.small.fontSize,
    },
  };

  return mergeStyleSets({
    root,
    thumbnail,
    thumbnailIcon,
    content,
    topLine,
    label,
    pricing,
    priceInfo,
    details,
    heading,
    notification,
    action,
  });
}

export const CartProductListing: React.FunctionComponent<ICartProductListingProps> = (props) => {
  const theme = getTheme();
  const cs = getStyleClasses(theme, props.theme, !!props.onSelected);
  const thumbnailStyle = !!props.imageUrl ? { backgroundImage: `url(${props.imageUrl})` } : undefined;
  const showingPrice = typeof props.pricing === 'number';
  return (
    <div className={cs.root}>
      <div
        className={cs.thumbnail}
        style={thumbnailStyle}
        aria-hidden={true}
        onClick={props.onSelected}
      >
        {!props.imageUrl &&
        <Icon
          className={cs.thumbnailIcon}
          iconName={'Photo2'}
        />}
      </div>
      <div className={cs.content}>
        <div className={cs.topLine}>
          <label
            className={cs.label}
            onClick={props.onSelected}
          >
            <i/>
            {props.label}
          </label>
          <div className={cs.pricing}>
            {props.showNotification &&
            <Icon
              aria-label={'You have a notification about this door'}
              className={cs.notification}
              iconName='RingerSolid'
              onClick={props.onSelected}
            />}
            {showingPrice &&
            <PriceDisplay
              price={props.pricing as number}
              fontSize={theme.fonts.mediumPlus}
            />}
            {!!props.pricing && showingPrice === false &&
            <span className={cs.priceInfo}>
              {props.pricing as string}
            </span>}
          </div>
        </div>
        <div className={cs.details}>
          <h3
            className={cs.heading}
            onClick={props.onSelected}
          >
            {props.title}
          </h3>
          <div className={cs.action}>
            <div>
              {!!props.variant &&
              <ProductVariantLabel
                texture={props.variant}
                size={1.4}
              />}
            </div>
            {(!!props.onExclude || !!props.onInclude) &&
            <BwjLinkButton
              text={!!props.onExclude ? 'Remove from project' : 'Restore to project'}
              icon={!!props.onExclude ? 'Delete' : 'Refresh'}
              onClick={props.onExclude ?? props.onInclude ?? (() => {})}
            />}
          </div>
        </div>
      </div>
    </div>
  );
};
